import * as React from 'react'
import { useState } from 'react'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'

import styles from './ConfirmDialog.pcss'
import { useIntl } from 'react-intl'

type ConfirmVariant = 'default' | 'danger'

interface ConfirmDialogProps {
  message: string | React.ReactNode
  children: (confirm: (callback: any, onBeforeOpen?: () => void) => (e: any) => void) => React.ReactElement<any>
  confirmLabel: string | React.ReactNode
  variant?: ConfirmVariant
  secondaryLabel?: string | React.ReactNode
  onSecondary?: () => void
  onDecline?: () => void
  disableConfirm?: boolean
  hideConfirm?: boolean
  className?: string
  // Override when a view renders more than one ConfirmDialog, so each is addressable in tests.
  'data-testid'?: string
}

const DEFAULT_TEST_ID = 'confirm-dialog'

export const ConfirmDialog = React.memo((props: ConfirmDialogProps) => {
  const intl = useIntl()
  const [active, setActive] = useState(false)
  const [submit, setSubmit] = useState<{ handler:(args: any) => any, params: any }>({ handler: () => null, params: null })

  const openDialog = (submitHandler: any, onBeforeOpen?: () => void) => (event: Event) => {
    if (event?.preventDefault && event?.stopPropagation) {
      event.preventDefault()
      event.stopPropagation()
    }

    if (props.disableConfirm) {
      submitHandler()
    } else {
      setSubmit({ handler: submitHandler, params: event })
      onBeforeOpen?.()
      setActive(true)
    }
  }

  const handleSubmit = (e: any) => {
    e.preventDefault()
    e.stopPropagation()
    if (submit.handler) {
      submit.handler(submit.params)
    }
    setActive(false)
  }

  const handleSecondary = () => {
    props.onSecondary?.()
    setActive(false)
  }

  const handleCancel = () => {
    props.onDecline?.()
    setActive(false)
  }

  const isDanger = props.variant === 'danger'
  const labelConfirm = props.confirmLabel
  const labelCancel = intl.formatMessage({ id: 'actions.cancel' })

  return (
    <React.Fragment>
      {props.children(openDialog)}
      <Dialog
        open={active}
        disableEscapeKeyDown
        classes={{ paper: `${styles.dialog} ${props.className || ''}`, root: styles.root }}
        data-testid={props['data-testid'] || DEFAULT_TEST_ID}
      >
        <h4 className={styles.message}>{props.message}</h4>
        <div className={styles.actions}>
          <Button onClick={handleCancel}>{labelCancel}</Button>
          {props.secondaryLabel && (
            <Button className={styles['btn-secondary']} onClick={handleSecondary}>{props.secondaryLabel}</Button>
          )}
          {!props.hideConfirm && (
            <Button
              variant="contained"
              color="primary"
              className={`${styles['btn-submit']} ${isDanger ? styles['btn-danger'] : ''}`}
              onClick={handleSubmit}
            >
              {labelConfirm}
            </Button>
          )}
        </div>
      </Dialog>
    </React.Fragment>
  )
})

export default ConfirmDialog
